import React, {
  memo, useEffect, useRef, useState,
} from 'react';
import { GrClose } from 'react-icons/gr';
import { FiArrowRight } from 'react-icons/fi';
import { IoChevronForward, IoChevronBack } from 'react-icons/io5';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Autoplay, Navigation, Scrollbar } from 'swiper';

import 'swiper/swiper-bundle.min.css';

SwiperCore.use([Navigation, Autoplay, Scrollbar]);

const validateString = (str) => (
  (str && str.trim() !== '') ? str : undefined
);

function ModalFullScreen({
  settings,
  closeModal,
  currentProduct,
}) {
  const backdropRef = useRef();
  const swiperRef = useRef();

  const [transitionedEnd, setTransitionedEnd] = useState(false);
  const [currentProductIndex, setCurrentProductIndex] = currentProduct;

  useEffect(() => {
    const { swiper } = swiperRef.current || {};
    if (swiper) {
      if (!swiper.autoplay.running) {
        swiper.autoplay?.start();
      }
      swiper.update();
    }
  }, [currentProductIndex]);

  useEffect(() => {
    backdropRef.current.addEventListener('transitionend', closeModal);
    return () => {
      backdropRef.current?.removeEventListener('transitionend', closeModal);
    };
  }, []);

  const onSlideSwiper = (translate) => {
    if (settings.featured_image_position === 'left' && translate < 0) {
      translate = Math.abs(translate);
    } else if (settings.featured_image_position === 'left' && translate > 0) {
      translate = -translate;
    }
    const { swiper } = swiperRef.current;
    swiper.autoplay.running && swiper.autoplay.stop();
    swiper.translateTo(swiper.getTranslate() + translate, 300, true, true);
    swiper.updateProgress();
  };

  const productLabels = settings.stl_products.map((el, i) => {
    const isActive = currentProductIndex === i;
    const itemClass = ['modalfs__item'];
    if (isActive) {
      itemClass.push('modalfs__item--active');
    }
    return (
      <li className={itemClass.join(' ')} key={Date.now() + i}>
        <div className="modalfs__group" onClick={() => setCurrentProductIndex(i)}>
          <FiArrowRight className="modalfs__icon-arrow" />
          {el.title}
        </div>
        {(settings.show_product_count && el.products && isActive) && (
          `${el.products.length} Products`
        )}
      </li>
    );
  });

  const activeProduct = settings.stl_products[currentProductIndex];

  const productSlides = activeProduct.products.map((el, i) => (
    <SwiperSlide key={i} data-swiper-autoplay="1">
      <a
        href={validateString(el.url)}
        title={validateString(el.title)}
        className="modalfs__link"
      >
        <img
          className="modalfs__img modalfs__img--sm"
          src={window.resizeImage(el.featured_image, 600)}
          alt={el.title}
        />
      </a>
    </SwiperSlide>
  ));

  const modalClass = [
    'modalfs',
    `modalfs--featured-${settings.featured_image_position}`,
  ];

  const loopSpeed = 15000 * +settings.modal_loop_animation_speed / 100;

  if (transitionedEnd) {
    modalClass.push('modalfs--transition');
  }

  return (
    <>
      <div className={modalClass.join(' ')}>
        <div className="modalfs__left">
          <div className="modalfs__head">
            <ul className="modalfs__list">
              {productLabels}
            </ul>
            <div className="modalfs__controls">
              <button
                className="modalfs__btn-slide modalfs__btn-slide--prev"
                onClick={() => onSlideSwiper(250)}
              >
                <IoChevronBack className="modalfs__icon-chevron" />
              </button>
              <button
                className="modalfs__btn-slide modalfs__btn-slide--next"
                onClick={() => onSlideSwiper(-250)}
              >
                <IoChevronForward className="modalfs__icon-chevron" />
              </button>
            </div>
          </div>
          <div className="modalfs__body">
            <Swiper
              dir={settings.featured_image_position === 'left' ? 'rtl' : undefined}
              className="modalfs__slider"
              ref={swiperRef}
              freeMode
              spaceBetween={1.5}
              slidesPerView={2}
              preloadImages
              loop={{
                enabled: true,
                momentumBounce: false,
              }}
              resistanceRatio={0}
              autoplay={{
                delay: 1,
                stopOnLastSlide: false,
                waitForTransition: false,
              }}
              speed={loopSpeed}
              breakpoints={{
                1025: {
                  preloadImages: true,
                  simulateTouch: false,
                },
              }}
            >
              {productSlides}
            </Swiper>
          </div>
        </div>
        <div className="modalfs__right">
          <img
            draggable="false"
            className="modalfs__img"
            src={window.resizeImage(activeProduct.featured_image, 1200)}
            alt="Product Image"
          />
        </div>
        <button
          className="modalfs__btn"
          type="button"
          onClick={() => setTransitionedEnd(true)}
        >
          <GrClose className="modalfs__icon-close" />
        </button>
      </div>
      <div ref={backdropRef} className="modalfs__backdrop" />
    </>
  );
}

export default memo(ModalFullScreen);
